import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Coins, Check, Loader2, Sparkles } from "lucide-react";
import { PageHero } from "../components/FormKit";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export const CoinsPage = () => {
  const { user, authApi } = useAuth();
  const [params] = useSearchParams();
  const [packs, setPacks] = useState([]);
  const [balance, setBalance] = useState(null);
  const [busy, setBusy] = useState(false);
  const sessionId = params.get("session_id");
  const success = window.location.pathname.includes("/coins/success");

  useEffect(() => { api.get("/coins/packs").then(({ data }) => setPacks(data.packs || [])); }, []);
  useEffect(() => { if (user) authApi.get("/coins/wallet").then(({ data }) => setBalance(data.balance)).catch(() => {}); }, [user]);

  useEffect(() => {
    if (!success || !sessionId || !user) return;
    let tries = 0;
    const poll = async () => {
      tries++;
      try {
        const { data } = await authApi.get(`/coins/status/${sessionId}`);
        if (data.credited) { setBalance(data.balance); toast.success("TAVEN Coins added to your wallet!"); return; }
      } catch {}
      if (tries < 6) setTimeout(poll, 1500);
    };
    poll();
  }, [success, sessionId, user]);

  const buy = async (i) => {
    if (!user) { toast.error("Please sign in to buy coins."); return; }
    setBusy(true);
    try { const { data } = await authApi.post("/coins/buy", { pack_index: i, origin_url: window.location.origin }); window.location.href = data.url; }
    catch { toast.error("Could not start checkout."); setBusy(false); }
  };

  return (
    <div data-testid="coins-page">
      <PageHero eyebrow="TAVEN Coins" title="TAVEN Coins Wallet" subtitle="Buy TAVEN Coins to send gifts and support your favorite live hosts. Coins are for digital gifts only — separate from product, course and affiliate payments.">
        <div className="flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-2.5" data-testid="coins-balance">
          <Coins className="w-5 h-5 text-taven-gold" /><span className="font-black font-display text-lg">{balance ?? "—"}</span><span className="text-xs uppercase tracking-wider text-white/70">coins</span>
        </div>
      </PageHero>

      <section className="py-10 px-4 md:px-8 lg:px-12 mx-auto max-w-5xl">
        {success && (
          <div className="border border-green-500 bg-green-50 p-4 mb-8 flex items-center gap-2 text-green-700" data-testid="coins-success">
            <Check className="w-5 h-5" /> Payment received — your coins are being added to your wallet.
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {packs.map((p, i) => (
            <div key={i} className="border border-black/10 bg-white p-6 text-center hover:border-taven-gold transition-colors" data-testid={`coin-pack-${i}`}>
              <Coins className="w-10 h-10 text-taven-gold mx-auto" />
              <p className="text-3xl font-black font-display mt-3">{p.coins}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">TAVEN Coins</p>
              {i > 0 && <p className="text-[11px] text-taven-gold font-bold mt-1 flex items-center justify-center gap-1"><Sparkles className="w-3 h-3" /> Best value</p>}
              <button onClick={() => buy(i)} disabled={busy} data-testid={`buy-pack-${i}`} className="mt-4 w-full bg-taven-orange text-white font-bold uppercase tracking-wider py-3 hover:bg-[#d9561a] disabled:opacity-60">${p.price.toFixed(2)}</button>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-6 text-center">Payments are processed securely. Coins are voluntary digital support items and are non-refundable except where required by law. Support: support@taven.store</p>
        <div className="text-center mt-6">
          <Link to="/rewards" data-testid="coins-to-rewards" className="inline-flex items-center gap-1.5 border border-taven-gold text-taven-black text-xs font-bold uppercase tracking-wider px-5 py-2.5 hover:bg-taven-gold/10 transition-colors">
            <Sparkles className="w-4 h-4 text-taven-gold" /> Redeem coins in your Rewards Wallet
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CoinsPage;
